"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Clock, Mail, Phone, Sparkles, UserCheck, Send } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const reassurancePoints = [{
  title: "100% Direct Access",
  description: "You work directly with me, Yassine. No junior staff or account managers."
}, {
  title: "Tailored SEO Strategy",
  description: "Custom roadmap designed specifically around your industry and commercial targets."
}, {
  title: "Transparent & Honest",
  description: "Clear deliverables, realistic timeframes, and measurable ROI reporting."
}, {
  title: "No Long Contracts",
  description: "Flexible, outcome-focused consultancy built on genuine trust and results."
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    resetForm,
    register,
    getValues
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-muted border border-border rounded-xl p-8 text-center space-y-4 my-6">
        <div className="size-14 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto">
          <CheckCircle2 className="size-8" />
        </div>
        <h4 className="text-xl font-bold text-foreground">Consultation Requested!</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Thank you, <span className="font-semibold text-foreground">{getValues("name")}</span>.
          I have received your details for <span className="font-semibold text-foreground">{getValues("website") || "your website"}</span> and will get back to you within 24 business hours.
        </p>
        <Separator className="my-4" />
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={resetForm} className="border-border hover:bg-background">
            Submit Another Query
          </Button>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/CaseStudiesPage">
              Explore Case Studies
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="cta-name" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Full Name <span className="text-destructive">*</span>
          </label>
          <input id="cta-name" type="text" required placeholder="e.g. Sarah Jenkins" {...register("name", {
          required: true
        })} className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="cta-email" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Work Email <span className="text-destructive">*</span>
          </label>
          <input id="cta-email" type="email" required placeholder="sarah@company.com" {...register("email", {
          required: true
        })} className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="cta-website" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Website URL <span className="text-destructive">*</span>
          </label>
          <input id="cta-website" type="url" required placeholder="https://yourcompany.com" {...register("website", {
          required: true
        })} className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="cta-service" className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Primary Focus Area
          </label>
          <select id="cta-service" {...register("service")} className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all">
            <option value="National SEO">National SEO</option>
            <option value="International SEO">International SEO</option>
            <option value="Local SEO">Local SEO</option>
            <option value="B2B SEO & Lead Gen">B2B SEO &amp; Lead Gen</option>
            <option value="Ecommerce SEO">Ecommerce SEO</option>
            <option value="Technical SEO">Technical SEO</option>
            <option value="SEO Audit">SEO Audit</option>
            <option value="AI Search Optimisation">AI Search Optimisation</option>
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="cta-message" className="text-xs font-semibold text-foreground uppercase tracking-wider">
          Tell Me About Your Goals <span className="text-muted-foreground font-normal">(Optional)</span>
        </label>
        <textarea id="cta-message" rows={3} placeholder="Share your current traffic situation, main competitors, or main pain points..." {...register("message")} className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none" />
      </div>

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base h-12 shadow-md transition-all duration-200">
          <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
          {isSubmitting ? "Booking Strategy Session..." : "Book a Free SEO Consultation"}
          <Send className="size-4 ml-2" />
        </Button>
      </div>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-accent shrink-0" />
          No obligation, 100% confidential discussion.
        </p>
        <Button asChild variant="link" className="text-xs text-primary p-0 h-auto hover:no-underline">
          <Link to="/ContactPage">
            Or go to Contact Page <ArrowRight className="size-3 inline ml-0.5" />
          </Link>
        </Button>
      </div>
    </form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="relative py-20 md:py-28 bg-background overflow-hidden text-foreground">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-6 space-y-8">
            <div className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
                <Sparkles className="size-3.5 text-accent mr-1.5" />
                Direct SEO Expertise
              </Badge>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready for Sustainable Search Growth?
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed">
                When you partner with YE Consultancy, you work directly with me — Yassine Essaadi.
                I provide hands-on technical SEO, tailored strategies, and transparent reporting
                to help your business rank higher and convert search traffic into genuine revenue.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex flex-col sm:flex-row items-center gap-5">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" className="size-20 rounded-full object-cover border-2 border-primary/20 shrink-0" />
              <div className="space-y-1 text-center sm:left">
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <span className="font-bold text-foreground text-lg">Yassine Essaadi</span>
                  <Badge variant="outline" className="text-xs bg-background text-foreground border-border">
                    <UserCheck className="size-3 text-primary mr-1" /> Freelance SEO Consultant
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  &quot;I personally oversee every audit, technical fix, and content strategy for my clients.
                  No middleman, no outsourced teams — just straightforward results.&quot;
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {reassurancePoints.map((point, i) => <div key={i} data-index={i} className="bg-card/60 border border-border/80 rounded-lg p-4 space-y-1">
                  <div className="flex items-center gap-2 font-semibold text-foreground text-sm">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{point.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal pl-6">
                    {point.description}
                  </p>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border space-y-3">
              <div className="text-sm font-medium text-foreground">
                Prefer to connect directly?
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium">
                  <Mail className="size-4 text-accent" />
                  <span>contact@ye-consultancy.com</span>
                </a>
                <a href="tel:+447490984430" className="inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium">
                  <Phone className="size-4 text-accent" />
                  <span>+44 7490984430</span>
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-6">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-xl relative">
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold font-serif text-card-foreground">
                    Book Your Free SEO Consultation
                  </h3>
                  <Badge variant="secondary" className="bg-accent/15 text-accent border border-accent/20">
                    <Clock className="size-3 mr-1" /> 30-Min Call
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Fill out the form below to claim your complimentary initial site review and discuss your growth goals.
                </p>
              </div>

              <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
              name: "",
              email: "",
              website: "",
              service: "National SEO",
              message: ""
            }} successMessage="Thank you. I have received your details and will get back to you within 24 business hours." errorMessage="Something went wrong. Please try again.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}